import { Component, ComponentFactoryResolver, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GridBaseComponent, SubContentService, SuffixCellRenderer } from '@cv-invision/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { NexsysMonitorService, OriginSourceData } from '../../service/nexsys-monitor.service';
// Models
import { CallbackLogsNexsysOrphanColumnsModel } from '../model/callbacklogs-nexsys-orphan-column.model';
import { CallbackFNCLogsColumnsModel, CallbackLogsColumnsModel } from '../model/callbacklogs-column.model';
import { CallbackLogsXpanseColumnsModel } from '../model/callbacklogs-xpanse-column.model';
import { ORIGIN, URLS } from '../model/nexsys-monitor.constant';
import { LogsDescriptionComponent } from './logs-description/logs-description.component';
import { CvDateFormatter } from '@cv-invision/cv.component-library.ux';
import { CustomActionNexsysOrphanReadComponent } from '../custom-grid-cell/custom-action-nexsys-orphan-read.component';
import { CustomActionNexsysOrphanDownloadComponent } from '../custom-grid-cell/custom-action-nexsys-orphan-download.component';
import { CustomActionNexsysHTTPStatusCodeComponent } from '../custom-grid-cell/custom-action-nexsys-http-status-code.component';
import { ValueLinkMonitorService } from '../../service/value-link.service';
import { FILTER_VALUELINK, LOG_DETAIL } from '../../value-link-monitor/model/value-link.model';
import { CONNEXIONS_LOG_DETAIL } from '../model/connexions.model';
import { ConnexionsMonitorService } from '../../service/connexions.service';
import { LENDINGQB_LOG_DETAIL } from '../model/lendingqb.model';

@Component({
	selector: 'cv-invision-callback-logs',
	templateUrl: './callback-logs.component.html',
	styleUrls: ['./callback-logs.component.scss']
})
export class CallbackLogsComponent extends GridBaseComponent implements OnInit, OnDestroy {
	filterLogs: any = {
		transactionId: '',
		providerOrderIdentifier: '',
		nexsysOrderId: '',
		xpanseSPNId: '',
		xpanseTransactionId: '',
		orderId: '',
		eventName: '',
		classOrderId: '',
		clientId: '',
		lendingQbOrderId:'',
		read: {
			id: 'All',
			name: 'All'
		},
		limit: 20
	};
	private atComponentDestroy: Subject<any> = new Subject<any>();
	private filtersChanged: any = new Subject<string | undefined>();
	private subscription: Subscription;
	private readonly debounceTime = 500;
	public origin: string;
	public sourceInputText: string;
	public isReadFilterOptions: Array<any> = [
		{
			id: 'All',
			name: 'All'
		},
		{
			id: 'Read',
			name: 'Read'
		},
		{
			id: 'Unread',
			name: 'Unread'
		}
	];

	constructor(
		private nexsysMonitorService: NexsysMonitorService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private subContentService: SubContentService,
		private router: Router,
		private dateService: CvDateFormatter,
		private valueLinkMonitorService: ValueLinkMonitorService,
		private connexionsMonitorService: ConnexionsMonitorService
	) {
		super();
		this.DEFAULT_ROW_HEIGHT = 40;
	}

	ngOnInit(): void {
		this.subContentService.close();

		this.setOrigin();
		
		this.sourceInputText = `${this.origin} Order Id`;

		switch (true) {
			case this.router.url.includes(URLS.FNC):
				this.gridOptions.columnDefs = new CallbackFNCLogsColumnsModel(this.origin, this.dateService).columns;
				break;
			case this.router.url.includes(URLS.XPANSE):
				this.gridOptions.columnDefs = new CallbackLogsXpanseColumnsModel(this.dateService).columns;
				break;
			case this.router.url.includes(URLS.VALUE_LINK):
				this.gridOptions.columnDefs = new LOG_DETAIL(this.dateService).columns;
				break;
			case this.router.url.includes(URLS.CONNEXIONS):
				this.gridOptions.columnDefs = new CONNEXIONS_LOG_DETAIL(this.dateService).columns;
				break;
			case this.router.url.includes(URLS.LENDINGQB):
				this.gridOptions.columnDefs = new LENDINGQB_LOG_DETAIL(this.dateService).columns;
				break;
			default:
				this.gridOptions.columnDefs = new CallbackLogsColumnsModel(this.origin, this.dateService).columns;
				break;
		}

		this.gridOptions.frameworkComponents = {
			suffixCellRenderer: SuffixCellRenderer,
			customActionNexsysOrphanReadComponent: CustomActionNexsysOrphanReadComponent,
			customActionNexsysOrphanDownloadComponent: CustomActionNexsysOrphanDownloadComponent,
			customActionNexsysHTTPStatusCodeComponent: CustomActionNexsysHTTPStatusCodeComponent

		};
		this.loadGridData();
		this.subscription = this.filtersChanged
			.pipe(debounceTime(this.debounceTime))
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(() => {
				this.onSearchLog();
			});

		// subscribe to the reload list event.
		this.nexsysMonitorService
			.getReloadListListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((params: OriginSourceData) => {
				if (params.reload) {
					this.loadGridData();
				}
			});
	}

	/**
	* Set the origin based on the URL
	*/
	setOrigin(): void {
		const url = this.router.url;
		switch (true) {
			case url.includes(URLS.NEXSYS):
				this.origin = ORIGIN.Nexsys;
				break;
			case url.includes(URLS.NEXSYS_ORPHAN):
				this.origin = ORIGIN.NexsysLegacy;
				break;
			case url.includes(URLS.VALUTRUST):
				this.origin = ORIGIN.Valutrust;
				break;
			case url.includes(URLS.XPANSE):
				this.origin = ORIGIN.Xpanse;
				break;
			case url.includes(URLS.ORDER_EXTERNAL):
				this.origin = ORIGIN.OrderExternal;
				break;
			case url.includes(URLS.FNC):
				this.origin = ORIGIN.FNC;
				break;
			case url.includes(URLS.EVO):
				this.origin = ORIGIN.EVO;
				break;
			case url.includes(URLS.VALUE_LINK):
				this.origin = ORIGIN.ValueLink;
				break;
			case url.includes(URLS.CONNEXIONS):
				this.origin = ORIGIN.Connexions;
				break;
			case url.includes(URLS.LENDINGQB):
				this.origin = ORIGIN.Lendingqb;
				break;
		}
	}


	/**
	 * On Filters change, do the search.
	 */
	onFiltersChange(filter: any, $event: any): void {

		if (filter === 'read' && $event === null) {
			$event = {
				id: 'All',
				name: 'All'
			};
		}

		this.filterLogs[filter] = typeof ($event.value) !== 'undefined' ? $event.value : $event;
		this.filtersChanged.next();
	}

	/**
	 *  Call service endpoint to search Products.
	 */
	loadGridData(): void {

		switch (true) {
			case this.origin === ORIGIN.ValueLink:
				this.valueLinkMonitorService.getLogs(this.filterLogs)
					.subscribe(data => this.handleData(data));
				break;
			case this.origin === ORIGIN.Connexions:
				this.connexionsMonitorService.getLogs(this.filterLogs)
					.subscribe(data => this.handleData(data));
				break;
			default:
				this.nexsysMonitorService
					.getLogs(this.filterLogs, this.origin)
					.pipe(takeUntil(this.atComponentDestroy))
					.subscribe(
						result => {
							const data = typeof (result['data']) !== 'undefined' ? result.data : result;
							this.handleData(data);
						},
						error => console.error(error)
					);
				break;
		}
	}

	/**
	 * Handle the response data and update the grid.
	 * @param res The response data from the service.
	 */
	private handleData(res): void {
		this.gridData = res;
		this.gridPagination.pagination.limit = res.length;
		this.gridPagination.pagination.totalCount = res.length;
		this.gridHeight = this.getGridHeight(res && res.length > 0 ? res.length : 0);
	}

	/**
	 * Selected order
	 */
	public onSelectionChanged(): void {
		const selectedRows = this.gridApi.getSelectedRows();
		const selectedRow = selectedRows[0];
		if (selectedRow) {
			this.openSubComponent(selectedRow);
		} else {
			this.subContentService.close();
		}
	}

	/**
	 * Open the component with the Selected Row
	 * @param selectedRow
	 */
	private openSubComponent(selectedRow: any = null): void {
		// Open logs Detail
		this.subContentService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: LogsDescriptionComponent,
				data: { ...selectedRow, source: this.origin }
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe();
	}

	/**
	 * searchby param logs
	 */
	onSearchLog(): void {
		this.gridApi.showLoadingOverlay();
		this.subContentService.close();
		this.loadGridData();
	}

	/**
	 * Clear all filters to it's default value.
	 */
	clearAllFilters(): void {
		this.filterLogs.limit = 20; //by default
		this.filterLogs.ValueLinkOrderId = '';
		this.filterLogs.ConnexionsOrderId = '';
		this.filterLogs.transactionId = '';
		this.filterLogs.providerOrderIdentifier = '';
		this.filterLogs.nexsysOrderId = '';
		this.filterLogs.orderId = '';
		this.filterLogs.eventName = '';
		this.filterLogs.classOrderId = '';
		this.filterLogs.clientId = '';
		this.filterLogs.lendingQbOrderId = '';
		this.filterLogs.read = {
			id: 'All',
			name: 'All'
		};
		this.filtersChanged.next();
	}

	/**
	 * On component destroy
	 */
	ngOnDestroy(): void {
		this.subContentService.close();
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
